import type { DeliveryRouteDay, DeliveryRouteResponse } from '@/api/delivery-route'
import { componentTypeGroup, hotterTemperature, PACK_TYPE_ORDER, temperatureGroupRank } from '@/lib/chefs/pack-groups'
import { type OrderedSheet, sheetsInRouteOrder } from '@/lib/chefs/print-words'
import type { Campus, Component, Temperature } from '@/lib/chefs/types'
import type { PagePlanTocSection } from '@/lib/print/page-plan'

// The generic pagination math (page geometry, resolvePlan) lives in the
// domain-agnostic module; re-exported here so chefs call sites keep their
// import path.
export type { ResolvedPlan, TocEntry } from '@/lib/print/page-plan'
export { PAGE_CONTENT_HEIGHT_PX, PAGE_CONTENT_WIDTH_PX, resolvePlan } from '@/lib/print/page-plan'

export interface PrintSections {
  summary: boolean
  ingredients: boolean
  recipes: boolean
  /** Cross-school packing view: each day's deliverables merged across every
   *  location with summed quantities (the Deliverables tab, printed).
   *  Optional — legacy callers never set it. */
  deliverables?: boolean
  /**
   * The driver's route sheet (the Delivery route): per delivery day, the
   * stops in order, when to leave and what to unload at each. Printed last,
   * after every other section. Optional: legacy callers never set it.
   */
  route?: boolean
}

export interface PrintDay {
  /** ISO YYYY-MM-DD — stable key for the day across renders. */
  iso: string
  date: Date
  /** Short label used on per-day campus pages, e.g. "Mon 05/25". */
  dateLabel: string
  /** Per-day campus list (multiplier = 1, with that day's off-campuses removed). */
  campuses: Campus[]
}

export interface PrintTarget {
  sections: PrintSections
  /** Selected days, chronological. Always length >= 1. */
  days: PrintDay[]
  kitchenName: string
  /** Full label for the cover page — "Mon 05/25 – Fri 05/29" or "Tuesday 05/26". */
  rangeDateLabel: string
  /** Counts surfaced on the cover index. */
  uniqueCampusCount: number
  uniqueRecipeCount: number
  /** Range-wide serving total across every campus (cover stat) — the
   *  production number Ops added up school-by-school. Optional so legacy
   *  builders keep compiling; absent hides the stat. */
  totalServings?: number
  /** When false, skip per-day separator pages (single-day Daily-view print). */
  showDayCovers: boolean
  /**
   * Set while a school on the printed days can still change its order: the
   * packet is PRELIMINARY (PROD-1452). Only the pages whose counts can still
   * change carry the "INCOMPLETE ORDER" watermark. Absent = counts are final.
   */
  preliminary?: PrintPreliminary
  /**
   * Pre-merged ingredient rows for the Ingredient List section. When
   * set (the shopping-list page passes the REAL `/api/shopping-list`
   * aggregation), the print skips the Campus-recipe-derived
   * `mergeAllIngredients` walk — adapted real-shipment campuses carry
   * no recipe data to walk.
   */
  ingredients?: Array<{
    key: string
    name: string
    unit: string
    quantity: number
    brand: string | null
    /** Grouping facts off the aggregation rows — see `MergedIngredient`. */
    category?: 'FOOD' | 'SUPPLY' | 'UNCATEGORIZED'
    usdaCategory?: string | null
  }>
  /**
   * The delivery route for these days (`/api/kitchen-orders/delivery-route`),
   * read only when `sections.route` is on. `undefined` = not asked for (or
   * still loading: the caller does not build a target until it lands);
   * `null` = it could not load, and the section prints one band saying so.
   */
  route?: DeliveryRouteResponse | null
}

/** Lock times as printed, per page scope. A scope missing from a map is final. */
export interface PrintPreliminary {
  /** The last open window in the packet: the cover, the ingredient list, the print confirm and the file name. */
  locksAtLabel: string
  /** Per printed day (`iso`): that day's deliverables, recipes and day covers, which total every school on it. */
  dayLocksAtLabel: Record<string, string>
  /** Per school order sheet (`campusBlockId`): only that school's own windows. */
  campusLocksAtLabel: Record<string, string>
}

// ---------------------------------------------------------------------------
// Stable block ids — shared between the renderer (which tags each leaf page
// with `data-print-block`) and the structure (which enumerates the same ids).
// One source of truth keeps measured heights aligned to plan blocks.
// ---------------------------------------------------------------------------
export const COVER_BLOCK_ID = 'cover'
export const INGREDIENTS_BLOCK_ID = 'ingredients'
export const ordersCoverBlockId = (iso: string) => `orders-cover|${iso}`
export const campusBlockId = (iso: string, campusId: string) => `campus|${iso}|${campusId}`
export const deliverablesBlockId = (iso: string) => `deliverables|${iso}`
export const recipesCoverBlockId = (iso: string) => `recipes-cover|${iso}`
export const recipeBlockId = (iso: string, key: string) => `recipe|${iso}|${key}`
export const routeBlockId = (iso: string) => `route|${iso}`
/** The one band the Delivery route section prints when the route could not load. */
export const ROUTE_UNAVAILABLE_BLOCK_ID = 'route|unavailable'

export type PrintBlockKind =
  | 'cover'
  | 'ingredients'
  | 'day-cover-orders'
  | 'campus'
  | 'day-cover-recipes'
  | 'recipe'
  | 'deliverables'
  | 'route'

export interface PlanBlock {
  /** Matches the `data-print-block` attr the renderer emits. */
  id: string
  kind: PrintBlockKind
  iso?: string
}

export interface PrintStructure {
  /** Render order, cover first. Every block starts on a fresh page. */
  blocks: PlanBlock[]
  toc: PagePlanTocSection[]
}

export interface AggregatedRecipe {
  key: string
  component: Component
  totalQty: number
  unit: string
  /** Canonical temperature of the deliverables the recipe rides in (the
   *  hottest when they differ); null on weekly-milk projections. */
  temperature: Temperature | null
}

/**
 * Browser print/PDF filename, derived from what the packet contains —
 * "Ordo_Orders_Mon 05/25 – Fri 05/29" instead of the app's bare "Ordo"
 * (which told the packing team nothing about what they were holding).
 */
export function printDocumentTitle(target: Pick<PrintTarget, 'sections' | 'rangeDateLabel' | 'preliminary'>): string {
  const { summary, ingredients, recipes, deliverables, route } = target.sections
  const label = summary
    ? 'Orders'
    : deliverables && !recipes && !ingredients
      ? 'Deliverables'
      : route && !deliverables && !recipes && !ingredients
        ? 'Delivery'
        : recipes && !ingredients
          ? 'Recipes'
          : ingredients && !recipes
            ? 'Ingredients'
            : 'Orders'
  // A preliminary copy says so in its file name too, so a saved PDF can't pass for the final one.
  return `Ordo_${label}_${target.rangeDateLabel}${target.preliminary ? '_INCOMPLETE' : ''}`
}

/** Long, book-style date label, e.g. "Monday, May 25". */
export function formatLongDate(date: Date): string {
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
}

/**
 * Unique recipe key for a component+unit pair. A recipe is counted once per
 * distinct (component name, deliverable unit) — matching the recipe pack and
 * day-cover aggregation.
 */
export function recipeKey(componentName: string, unit: string): string {
  return `${componentName.toLowerCase()}|${unit}`
}

/**
 * Recipes needed across a day's campuses, quantities summed and deduped by
 * (component name, unit). Shared by the recipe pack renderer and the plan so
 * recipe block ids line up with what actually gets printed.
 */
export function mergeDayRecipes(campuses: Campus[]): AggregatedRecipe[] {
  const merged = new Map<string, AggregatedRecipe>()
  const add = (component: Component, qty: number, unit: string, temperature: Temperature | null) => {
    // Recipe-less (buy-and-serve) components aren't something the kitchen
    // MAKES. Condiments are NOT excluded by kind: a house-made ranch or
    // salsa is a recipe the kitchen has to batch, and a purchased packet
    // drops out anyway because it carries no recipe.
    if (!component.recipe) {
      return
    }
    const key = recipeKey(component.name, unit)
    const existing = merged.get(key)
    if (existing) {
      // One recipe at two portions (a 1/2 Cup and a 1 Cup mac, PROD-1372)
      // stays ONE card — it is one batch — but scales by the qty-weighted
      // multiplier, so servings × multiplier is the food the portions need
      // instead of every serving at the first contributor's portion. Copied:
      // `component` is shared with the campus rows.
      const prior = existing.component.servingMultiplier ?? 1
      const next = component.servingMultiplier ?? 1
      if (next !== prior && existing.totalQty + qty > 0) {
        existing.component = {
          ...existing.component,
          servingMultiplier: (existing.totalQty * prior + qty * next) / (existing.totalQty + qty),
        }
      }
      existing.totalQty += qty
      // One recipe can ride in deliverables of different temperatures — the
      // prep bench cares about the hot hold (mirrors recipes-by-day).
      existing.temperature = hotterTemperature(existing.temperature, temperature)
    } else {
      merged.set(key, { key, component, totalQty: qty, unit, temperature })
    }
  }
  for (const campus of campuses) {
    for (const block of campus.mealtimes) {
      for (const deliverable of block.deliverables) {
        for (const component of deliverable.components) {
          add(component, deliverable.qty, deliverable.unit, deliverable.temperature)
        }
      }
    }
    // Milk carries its own campus-level rows (PROD-1502); a recipe-backed
    // carton gets a card like any other item (same key space as package rows,
    // so a day serving the same carton in meal bundles merges into ONE card).
    for (const milk of campus.milk ?? []) {
      add(milk.component, milk.qty, 'x', milk.temperature)
    }
  }
  // Same default order as the on-screen Recipes page: TYPE (mains → sides →
  // condiments — a recipe-backed condiment sorts into the condiments group),
  // then canonical temperature (hot → chilled → ambient → unknown), then
  // name. The plan's
  // block enumeration and the pack renderer both fold through this function,
  // so pagination/TOC stay aligned with the reordering.
  return Array.from(merged.values()).sort((a, b) => {
    const typeDelta =
      PACK_TYPE_ORDER.indexOf(componentTypeGroup(a.component)) -
      PACK_TYPE_ORDER.indexOf(componentTypeGroup(b.component))
    if (typeDelta !== 0) {
      return typeDelta
    }
    const tempDelta = temperatureGroupRank(a.temperature) - temperatureGroupRank(b.temperature)
    if (tempDelta !== 0) {
      return tempDelta
    }
    return a.component.name.localeCompare(b.component.name)
  })
}

/** Count distinct recipes needed across a day's campuses (recipe-less items excluded). */
export function countUniqueRecipes(campuses: Campus[]): number {
  return mergeDayRecipes(campuses).length
}

/** The longest window the route endpoint serves (`DELIVERY_ROUTE_MAX_DAYS` on the server), first and last day counted. */
export const PRINT_ROUTE_MAX_DAYS = 31

/**
 * The window to read the route for, for a packet's picked days (`YYYY-MM-DD`):
 * their first to their last. Null when there are none, or when they span more
 * than the endpoint serves (a long custom range): the route section then
 * prints its band.
 */
export function routeFetchWindow(isos: readonly string[]): { startDate: string; endDate: string } | null {
  if (isos.length === 0) {
    return null
  }
  const sorted = [...isos].sort()
  const startDate = sorted[0]!
  const endDate = sorted[sorted.length - 1]!
  const days = Math.round((Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / 86_400_000) + 1
  return days <= PRINT_ROUTE_MAX_DAYS ? { startDate, endDate } : null
}

/** The route's day for a print day, or null (no route, or nothing on it). */
export function routeDayFor(target: Pick<PrintTarget, 'route'>, iso: string): DeliveryRouteDay | null {
  return target.route?.days.find((day) => day.date === iso) ?? null
}

/** A route day with something on it: a stop to drive to, or a dry run to name. */
function routeDayHasStops(day: DeliveryRouteDay | null): day is DeliveryRouteDay {
  return day !== null && (day.stops.length > 0 || day.notDriven.length > 0)
}

/**
 * The Delivery route section's days: each print day the route has a stop
 * on, with its route day. Empty when the route was not asked for or could not
 * load (see `routeUnavailable`).
 */
export function routePrintDays(target: PrintTarget): Array<{ day: PrintDay; route: DeliveryRouteDay }> {
  if (!target.sections.route || !target.route) {
    return []
  }
  return target.days.flatMap((day) => {
    const route = routeDayFor(target, day.iso)
    return day.campuses.length > 0 && routeDayHasStops(route) ? [{ day, route }] : []
  })
}

/** The Delivery route section was asked for but its data could not load: it prints one band that says so. */
export function routeUnavailable(target: Pick<PrintTarget, 'sections' | 'route'>): boolean {
  return Boolean(target.sections.route) && target.route === null
}

/** "Thu, Oct 15": the day on a summary sheet's stop line. */
export function shortPrintDate(date: Date): string {
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

/** The right side of a summary sheet's campus row: "Stop 1 · Thu, Oct 15". */
export function summaryStopLine(stop: number, date: Date): string {
  return `Stop ${stop} · ${shortPrintDate(date)}`
}

/**
 * A day's summary sheets in the order the van unloads (target print, Oct 14):
 * the route's stops in its order, each with "Stop N · Thu, Oct 15", then any
 * campus off the route. Without route data (the Delivery route section is
 * off, or it could not load) the sheets keep the day's own campus order (the
 * Orders screen's `sortCampusesForOrders`, so the packet matches the screen it
 * was printed from) and carry no stop line. `buildPrintStructure` and
 * `PrintRoot` both read this, so the plan's blocks and the pages stay in one
 * order.
 */
export function orderedSheetsFor(
  target: Pick<PrintTarget, 'route' | 'sections'>,
  day: PrintDay,
): OrderedSheet<Campus>[] {
  const routeDay = target.sections.route ? routeDayFor(target, day.iso) : null
  return sheetsInRouteOrder(day.campuses, routeDay).map((sheet) => ({
    ...sheet,
    stopLine: sheet.stop !== null ? summaryStopLine(sheet.stop, day.date) : null,
  }))
}

/**
 * Ordered list of every leaf page block the packet will render, plus the TOC
 * sections and which block each points at. Pure — no page numbers yet, because
 * those depend on measured heights (see resolvePlan).
 *
 * The order MUST be `PrintRoot`'s render order (cover → ingredients →
 * deliverables (optional, off by default) → summary per stop → recipes →
 * route, the target print's order): page numbers accumulate in this order, so
 * a section listed out of order gets every later section's numbers wrong.
 */
export function buildPrintStructure(target: PrintTarget): PrintStructure {
  const { sections, days, showDayCovers } = target
  const nonEmptyDays = days.filter((d) => d.campuses.length > 0)

  const blocks: PlanBlock[] = [{ id: COVER_BLOCK_ID, kind: 'cover' }]
  const toc: PagePlanTocSection[] = []

  if (sections.ingredients) {
    blocks.push({ id: INGREDIENTS_BLOCK_ID, kind: 'ingredients' })
    toc.push({ label: 'Ingredient list', firstBlockId: INGREDIENTS_BLOCK_ID })
  }

  if (sections.deliverables) {
    for (const day of nonEmptyDays) {
      const id = deliverablesBlockId(day.iso)
      blocks.push({ id, kind: 'deliverables', iso: day.iso })
      toc.push({ label: `Deliverables for ${formatLongDate(day.date)}`, firstBlockId: id })
    }
  }

  // The summary: one sheet per campus, in stop order, each listed under its
  // day so a packer can pull one pile's sheet from the Contents.
  if (sections.summary) {
    for (const day of nonEmptyDays) {
      const sheets = orderedSheetsFor(target, day)
      const coverId = ordersCoverBlockId(day.iso)
      const firstCampusId = campusBlockId(day.iso, sheets[0]!.campus.id)
      if (showDayCovers) {
        blocks.push({ id: coverId, kind: 'day-cover-orders', iso: day.iso })
      }
      toc.push({
        label: `Summary for ${formatLongDate(day.date)}`,
        firstBlockId: showDayCovers ? coverId : firstCampusId,
      })
      for (const sheet of sheets) {
        const id = campusBlockId(day.iso, sheet.campus.id)
        blocks.push({ id, kind: 'campus', iso: day.iso })
        toc.push({
          label: sheet.stop !== null ? `Stop ${sheet.stop} · ${sheet.campus.name}` : sheet.campus.name,
          firstBlockId: id,
          depth: 1,
        })
      }
    }
  }

  if (sections.recipes) {
    for (const day of nonEmptyDays) {
      const recipes = mergeDayRecipes(day.campuses)
      if (recipes.length === 0 && !showDayCovers) {
        continue
      }
      const coverId = recipesCoverBlockId(day.iso)
      if (showDayCovers) {
        blocks.push({ id: coverId, kind: 'day-cover-recipes', iso: day.iso })
      }
      const firstRecipeId = recipes[0] ? recipeBlockId(day.iso, recipes[0].key) : coverId
      toc.push({
        label: `Recipes for ${formatLongDate(day.date)}`,
        firstBlockId: showDayCovers ? coverId : firstRecipeId,
      })
      for (const r of recipes) {
        blocks.push({ id: recipeBlockId(day.iso, r.key), kind: 'recipe', iso: day.iso })
      }
    }
  }

  // The driver's sheet goes last: it is what leaves the kitchen with the van.
  if (sections.route) {
    if (routeUnavailable(target)) {
      blocks.push({ id: ROUTE_UNAVAILABLE_BLOCK_ID, kind: 'route' })
      toc.push({ label: 'Delivery route', firstBlockId: ROUTE_UNAVAILABLE_BLOCK_ID })
    }
    for (const { day } of routePrintDays(target)) {
      const id = routeBlockId(day.iso)
      blocks.push({ id, kind: 'route', iso: day.iso })
      toc.push({ label: `Delivery route for ${formatLongDate(day.date)}`, firstBlockId: id })
    }
  }

  return { blocks, toc }
}

/**
 * Stable identity for a target's selection — lets callers cache measurements.
 * Every section toggle is part of it: a toggle left out reuses the other
 * selection's measurement (page count and contents numbers go stale).
 */
export function planSignature(target: PrintTarget): string {
  const isos = target.days.map((d) => d.iso).join(',')
  const { summary, ingredients, deliverables, recipes, route } = target.sections
  const flag = (on: boolean | undefined) => (on ? 1 : 0)
  // The route fills its own section: its stops (in order) and whether drive
  // times are in change the pages, so they are part of the identity too.
  const routeKey =
    target.route === undefined
      ? ''
      : target.route === null
        ? '|r!'
        : `|r${target.route.driveTimes.status}:${target.route.days
            .map((day) => `${day.date}=${day.stops.map((s) => s.schoolLocationId).join('.')}`)
            .join(',')}`
  // Every lock label is part of the selection: a packet captured while a school
  // was open must never be reused once it locks (or vice versa).
  return `${isos}|s${flag(summary)}i${flag(ingredients)}v${flag(deliverables)}r${flag(recipes)}t${flag(route)}${routeKey}|d${flag(target.showDayCovers)}|p${target.preliminary ? JSON.stringify(target.preliminary) : ''}`
}
